import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Calendar } from 'primereact/calendar';
import { Container, Row, Col, Tabs, Tab, Spinner } from "react-bootstrap";
import { ContentHeader, ContentBody } from "./styles"
import { StatementsTable } from "../../components/table-statements";
import { getStatements, getOpenedStatements, getFinishedStatements } from "../../../../services/manager/partner/statements.service";
import { FaFolder } from "react-icons/fa";
import { PaginationList } from "../../../../components/pagination";
import { formatDateDB } from "../../../../../helpers";

export const Statements = (): JSX.Element => {
  const { t: translation } = useTranslation();

  //Pagination
  const [totalItens, setTotalItens] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)

  //Ordering and searching
  const [query, ] = useState("");
  const [sortOrder, setSortOrder] = useState(1);
  const [sortField, setSortField] = useState("date");

  const [showEmptyMessage, setShowEmptyMessage] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [key, setKey] = useState<string>("all");
  const [period, setPeriod] = useState<Date | Date[] | undefined>(undefined);

  const [statementList, setStatementList] = useState([]);
  const [openedStatements, setOpenedStatements] = useState([]);
  const [finishedStatements, setFinishedStatements] = useState([]);

  const treeTableConfig = [
    {field: 'created_at', header: 'Data', dateTemplate: true, sortable: true, style: {textAlign: 'center', width: '15%'}},
    {field: 'description', header: 'Descrição', sortable: false},
    {field: 'balance', header: 'Valor em R$', valueTemplate: true, sortable: true, style: {textAlign: 'center', width: '30%'}},
  ]

  const handleChangeTab = (k: string) => {
    setPeriod([])
    switch (k) {
      case 'entry':
        getOpenedList();
      break;

      case 'exit':
        getFinishedList();
      break;
      
      default:
        getStatementsList();
    }
    setKey(k)
  }

  const handleTableSort = useCallback((event: any) => {
    let order = event.sortOrder > 0 ? event.sortField : '-' + event.sortField;
    
    setSortOrder(event.sortOrder);
    setSortField(event.sortField);

    switch (key) {
      case 'entry':
        getOpenedList(`ordering=${order}`);
      break;

      case 'exit':
        getFinishedList(`ordering=${order}`);
      break;
      
      default:
        getStatementsList(`ordering=${order}`)
    }
  }, []);

  const handlePageChange = (event?: any) => {
    if (currentPage !== event) {
      switch (key) {
        case 'entry':
          getOpenedList(`page=${event}&q=${query}`);
        break;

        case 'exit':
          getFinishedList(`page=${event}&q=${query}`);
        break;
        
        default:
          getStatementsList(`page=${event}&q=${query}`)
      }
      setCurrentPage(event)
    }
  }

  const handleSelectPeriod = (_period: any) => {
    setPeriod(_period)
    if (_period[1] !== null) {
      let startDate = formatDateDB(_period[0]) 
      let endDate = formatDateDB(_period[1])
      
      switch (key) {
        case 'entry':
          getOpenedList(`start=${startDate}&end=${endDate}`);
        break;
  
        case 'exit':
          getFinishedList(`start=${startDate}&end=${endDate}`);
        break;
        
        default:
          getStatementsList(`start=${startDate}&end=${endDate}`)
      }
    }
  }

  const getStatementsList = (_query?: any) => {
    setLoading(true)
    getStatements(_query).then((response) => {
      if (response.length === 0) {
        setShowEmptyMessage(true)
      } else {
        setShowEmptyMessage(false)
      }
      setStatementList(response)
      setTotalItens(response.count);
      setLoading(false)
    })
  }

  const getOpenedList = (_query?: any) => {
    setLoading(true)
    getOpenedStatements(_query).then((response) => {
      if (response.length === 0) {
        setShowEmptyMessage(true)
      } else {
        setShowEmptyMessage(false)
      }
      setOpenedStatements(response)
      setTotalItens(response.count);
      setLoading(false)
    })
  }

  const getFinishedList = (_query?: any) => {
    setLoading(true)
    getFinishedStatements(_query).then((response) => {
      if (response.length === 0) {
        setShowEmptyMessage(true)
      } else {
        setShowEmptyMessage(false)
      }
      setFinishedStatements(response)
      setTotalItens(response.count);
      setLoading(false)
    })
  }

  useEffect(() => {
    getStatementsList();
  }, [])

  return (
    <Container fluid>
      <ContentHeader>
        <Row>
          <Col>
            <div className="title">
              {translation('manager.statements.statements')}
            </div>
          </Col>
          <Col className="text-end">
            <label htmlFor="range">{translation('manager.statements.period')}:</label>
            <Calendar 
              className="ml-2 mb-3 select-period-input"
              showIcon
              placeholder={translation('manager.statements.period')}
              dateFormat="dd/mm/yy"
              locale={'pt-BR'} 
              value={period} 
              onChange={(e: any) => handleSelectPeriod(e.value)} 
              selectionMode="range" 
              readOnlyInput 
            />
          </Col>
        </Row>
      </ContentHeader>

      <ContentBody>
        <Row className="mt-3">
          <div id="tabs-materials">
            <Tabs className="tabs mb-3" activeKey={key} onSelect={(k: any) => handleChangeTab(k)}>
              <Tab eventKey="all" title={translation('manager.statements.all')}>
                {
                  loading ? (
                    <div className="loading-container mt-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <>
                      {
                        showEmptyMessage ? (
                          <div className="text-center text-grey mt-5">
                            <FaFolder className="mt-5" size={100} />
                            <h5>{translation("manager.statements.emptyStatements")}</h5>
                          </div>
                        ) : (
                          <StatementsTable
                            treeFields={treeTableConfig} 
                            nodes={statementList}
                            sortField={sortField} 
                            sortOrder={sortOrder}
                            handleTableSort={handleTableSort}
                          />
                        )
                      }
                    </>
                  )
                }
              </Tab>
              <Tab eventKey="entry" title={translation('manager.statements.entry')}>
                {
                  loading ? (
                    <div className="loading-container mt-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <>
                      {
                        showEmptyMessage ? (
                          <div className="text-center text-grey mt-5">
                            <FaFolder className="mt-5" size={100} />
                            <h5>{translation("manager.statements.emptyStatements")}</h5>
                          </div>
                        ) : (
                          <StatementsTable
                            treeFields={treeTableConfig} 
                            nodes={openedStatements}
                            sortField={sortField} 
                            sortOrder={sortOrder}
                            handleTableSort={handleTableSort}
                          />
                        )
                      }
                    </>
                  )
                }
              </Tab>
              <Tab eventKey="exit" title={translation('manager.statements.exit')}>
                {
                  loading ? (
                    <div className="loading-container mt-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <>
                      {
                        showEmptyMessage ? (
                          <div className="text-center text-grey mt-5">
                            <FaFolder className="mt-5" size={100} />
                            <h5>{translation("manager.statements.emptyStatements")}</h5>
                          </div>
                        ) : (
                          <StatementsTable
                            treeFields={treeTableConfig} 
                            nodes={finishedStatements}
                            sortField={sortField} 
                            sortOrder={sortOrder}
                            handleTableSort={handleTableSort}
                          />
                        )
                      }
                    </>
                  )
                }
              </Tab>
            </Tabs>
          </div>
        </Row>

        {
          !showEmptyMessage &&
          <Row className="mt-5">
            <PaginationList currentPage={currentPage} totalItems={totalItens} onPageChange={handlePageChange} />
          </Row>
        }
      </ContentBody>
    </Container>
  )
}